import React, { useEffect, useState } from "react";
import { withLazy } from "./components/LazyRoute";
import { I18nextProvider, useTranslation } from "react-i18next";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
} from "react-router-dom";
import { connect, useDispatch } from 'react-redux';
import { useComplianceCountry } from "./hooks/useComplianceCountry";
// import "moment/min/locales";
import CurrentStatus from "./components/currentStatus";
import AIAssistant from "./components/ai-chat";
import OtpToast from "./components/notifications/OtpToast";
import NotificationPopup from "./components/notifications/NotificationPopup";
import moment from "moment";
import constants from "./assets/constants";
import { applyDocumentLocale } from "./utils/documentLocale";
import PrivateRoute from "./PrivateRoute";
import Callback from "./Callback";
import LogoutCallback from "./LogoutCallback";
import SilentRenewCallback from "./SilentRenewCallback";

// New layout system (Tailwind-based)
import { MasterLayout } from "./components/layout";
import { INTEGRATIONS_ROLES } from "./components/layout/navigationModules";
import { RoleProtectedRoute } from "./components/RoleProtectedRoute";
// Dev-only surfaces (seeding) and the upgrade-pitch surfaces (brochures) need a
// gate on the ROUTE, not on the link — both were reachable by typed URL.
import { DevToolsRoute } from "./components/DevToolsRoute";
import { SubscriptionAudienceRoute } from "./components/subscription/SubscriptionAudienceRoute";
import { ShiftSwapEnabledRoute } from "./components/ShiftSwapEnabledRoute";
import { HourlyPayRoute } from "./components/HourlyPayRoute";
import { ProfileAccessGuard } from "./components/ProfileAccessGuard";
import OnboardingGate from "./components/OnboardingGate";
import ErrorBoundary from "./components/ErrorBoundary";

// Roles that can access manager-level routes
const MANAGER_ROLES = ['Manager', 'Hr', 'Chief'];
// /ai-impact-analysis — MANAGER_ROLES plus Admin, kept in step with the nav entry in
// navigationModules.ts. Admin is here because Admin is a snapshot author server-side (`IsPrivileged`
// = Hr || Admin in PerformanceSnapshotService), so excluding it would hide a roster it can read.
// Operator is deliberately absent: this page reads company employee data (CLAUDE.md Role Model).
//
// 🔴 ADMIN STAYS, decided by the owner 2026-09-10. The argument for dropping it was that
// `SkillService.GetUserStrengths` refuses an Admin who is neither HR nor the person's manager, so an
// Admin met an unexplained empty section. That is true, and it is now handled where it belongs:
// `SkillsGrowthPanel` gives each of its three sources an independent state and renders a 403 as
// ABSENT rather than as an error. Removing the role here instead would leave the SPA and the server
// disagreeing, since the `"Hr,Chief,Manager"` policy on every endpoint this page calls admits Admin.
// Do not drop Admin from one side alone.
const AI_IMPACT_ROLES = [...MANAGER_ROLES, 'Admin'];
// Roles that can access operator-level routes (system-wide settings)
const OPERATOR_ROLES = ['Operator'];
// Roles that can access settings routes (HR and Admin). Operator is a subscription/platform-only
// actor (RBAC model) and must never reach company settings/config — it uses operator-specific routes.
const SETTINGS_ROLES = ['Hr', 'Admin'];
// Roles that can access HR-only routes
const HR_ONLY_ROLES = ['Hr'];
// CO-239 — the "nobody" role set, used to close a route whose FEATURE is off rather than whose
// AUDIENCE is wrong. RoleProtectedRoute already handles an unmatched role set (brief notice, then
// redirect home), so an environment-disabled route needs no second guard component. Module-level so
// the reference is stable across renders.
const NO_ROLES: string[] = [];
// Assets page. Hr is an asset manager implicitly (full write); AssetManagement-tagged users
// (any primary role) get the same. Admin is NOT implicitly an asset manager — Admin must be
// granted the tag like any other employee, and then arrives here as 'AssetManagement'.
// See hooks/useCanManageAssets.ts for the single client-side derivation of this model.
const ASSETS_PAGE_ROLES = ['Hr', 'AssetManagement'];
// CO-365 — "every logged-in user EXCEPT Operator". The set that was missing from this file: employee
// self-service leave surfaces are company employee data, so Operator must not reach them (Role Model in
// CLAUDE.md), but a MANAGER_ROLES gate would lock out the Clocking employees they exist for. Leaving the
// route unguarded was the false dichotomy between those two. Mirrors ROLES.CLOCKING in
// navigationModules.ts:15 (which already hides the sidebar entry from Operator) and the
// RecurringLeaveRoles constant on LeaveController. Keep the three in step.
const EMPLOYEE_SELF_SERVICE_ROLES = ['Clocking', 'Manager', 'Hr', 'Chief', 'Admin'];
// Roles that can access HR + Operator routes (e.g. Compliance Config)
const HR_AND_OPERATOR_ROLES = ['Hr', 'Operator'];
// HR + Admin (company-wide management)
const HR_AND_ADMIN_ROLES = ['Hr', 'Admin'];
// CO-322 — the SERVER scopes the data (HR/Admin: own company; Operator: CompanyId-null platform events only).
const SECURITY_AUDIT_ROLES = ['Hr', 'Admin', 'Operator'];
// Core HR module audience — managers (incl. team-level) + Admin.
// Mirrors the `people` module roles in navigationModules.ts ([...MANAGER, 'Admin']).
const CORE_HR_ROLES = ['Manager', 'Hr', 'Chief', 'Admin'];
// Team-role settings write to HR-only endpoints; Manager would reach a blank page.
const CORE_HR_SETTINGS_ROLES = ['Hr', 'Chief', 'Admin'];
// Hourly pay §4.3 — who decides a shift: the employee's able direct managers (synthetic 'Manager'
// for anyone with direct reports, whatever their primary role) or HR. Chief/Admin without reports
// decide nothing, so they get no queue. Keep in step with ROLES.SHIFT_APPROVERS in navigationModules.ts.
const SHIFT_APPROVER_ROLES = ['Manager', 'Hr'];

import CompanyDashboard from "./views/CompanyDashboard";
import HomeRoute from "./views/CompanyDashboard/HomeRoute";
const PendingActions = withLazy(() => import("./views/PendingActions"));
const AIConsultant = withLazy(() => import("./views/AIConsultant"));
const AIConsultantHistory = withLazy(() => import("./views/AIConsultant/History"));
// Lazy on purpose: the page ships an ~180 KB static catalogue of 88 reviewed chatbot use cases.
// Imported eagerly it would sit in the main bundle for every user of every tenant, and almost
// nobody opens it.
const ChatbotSettingsAnalysis = withLazy(() => import("./views/Settings/General/ChatbotSettingsAnalysis"));
import NotFound from "./views/General/error/404";
import Notifications from "./views/General/notifications";
import Organizations from "./views/Settings/Organizations";
import AddOrganization from "./views/Settings/Organizations/AddOrganization";
import Companies from "./views/Settings/Companies";
import AddUser from "./views/EmployeesCycle/General/add-user";
import OnDemandNotification from "./views/EmployeesCycle/General/onDemandNotification";
import SignupFree from "./views/General/Signup/SignupFree";
import PlatformTermsIndex from "./views/Settings/PlatformTerms/PlatformTermsIndex";
import PrivacyPolicy from "./views/General/privacyPolicy";
import Widget from "./views/General/widget";

const Attendance = withLazy(() => import("./views/Attendance/attendance"));
import AttendanceOverview from "./views/Attendance/AttendanceOverview";
import AttendanceCorrectionRequests from "./views/Attendance/CorrectionRequests";
// LeavesAndHolidays removed – legacy page deleted, routes redirect to /my-leaves
import Reports from "./views/Attendance/Reports";
const Maps = withLazy(() => import("./views/Attendance/map"));
import Dashboard from "./views/Attendance/Dashboard";
import WorkSchedules from "./views/Attendance/WorkSchedules";
import RecurringSchedules from "./views/Attendance/WorkSchedules/RecurringSchedules";
import TeamAttendancePage from "./views/Attendance/team-attendance-page";

import ClockingsReport from "./views/Attendance/Reports/tabs/Clockings";
const ShiftsReport = withLazy(() => import("./views/Attendance/Reports/tabs/Shifts"));
const OvertimeReport = withLazy(() => import("./views/Attendance/Reports/tabs/Overtime"));
const OutOfBoundsReport = withLazy(() => import("./views/Attendance/Reports/tabs/OutOfBounds"));
const LeavesReport = withLazy(() => import("./views/Attendance/Reports/tabs/Leaves"));
const LeaveBalanceReport = withLazy(() => import("./views/Attendance/Reports/tabs/LeaveBalance"));
// CO-583 — the overtime approval QUEUE. A sibling of the Overtime REPORT, not a tab on it: one row
// per SHIFT (the grain the decision endpoint writes at) rather than one per employee-day.
const OvertimeApprovals = withLazy(() => import("./views/Attendance/OvertimeApprovals"));
// Hourly pay (docs/hourly-pay-spec.md §6, §10) — the per-shift approval queue and the hourly
// employee's own shifts. Lazy: only approvers / hourly employees in an enabled company ever open them.
const ShiftApprovals = withLazy(() => import("./views/Attendance/ShiftApprovals"));
const MyShifts = withLazy(() => import("./views/Attendance/MyShifts"));
import WorkSessionsReport from "./views/Attendance/Reports/tabs/WorkSessions";

import UsersStatusesReportExport from "./components/reportsDownload/UsersStatuses";
const ClockingsReportExport = withLazy(() => import("./components/reportsDownload/Clockings"));
const ShiftsReportExport = withLazy(() => import("./components/reportsDownload/Shifts"));
const OvertimeReportExport = withLazy(() => import("./components/reportsDownload/Overtime"));

import EmployeesGeneral from "./views/EmployeesCycle/General";
import SetUp from "./views/EmployeesCycle/SetUp";
import EmployeesGrouping from "./views/EmployeesCycle/EmployeeGrouping"
import SkillsOverview from "./views/EmployeesCycle/SkillsOverview"
import OrganizationalStructure from "./views/EmployeesCycle/OrganizationalStructure"

// Tournament Management imports
import TournamentList from "./views/TournamentManagement/tournament-list";
import TournamentMatches from "./views/TournamentManagement/tournament-matches";
import RefereeAssignmentCenter from "./views/TournamentManagement/referee-assignment-center";
import WorkPointSettings from "./views/TournamentManagement/work-point-settings";
import MyAssignments from "./views/TournamentManagement/my-assignments";
import Compensation from "./views/TournamentManagement/compensation";
import RefereeWorkload from "./views/TournamentManagement/referee-workload";

import DependentsDirectory from "./views/DependentsDirectory";
import TeamRoleManagement from "./views/TeamRoleManagement";

// Coming Soon preview pages (Plans 08-14)
import EmployeeDocumentCenter from "./views/ComingSoon/EmployeeDocumentCenter";
import EmploymentLifecycle from "./views/ComingSoon/EmploymentLifecycle";
import DepartmentsLocations from "./views/DepartmentsLocations";
import JobTitlesGrades from "./views/ComingSoon/JobTitlesGrades";
import CompanyFilesPolicies from "./views/ComingSoon/CompanyFilesPolicies";
import ProfileAuthorization from "./views/ComingSoon/ProfileAuthorization";
import DocumentTemplateManager from "./views/CoreHR/document-template-manager";
// TeamRoleManagement now imported from ./views/TeamRoleManagement (non-ComingSoon)
import HRProcessAutomation from "./views/ComingSoon/HRProcessAutomation";
import CoreHRReports from "./views/Reports/CoreHRReports";
import AttendanceDashboard from "./views/ComingSoon/AttendanceDashboard";
import DutyRosterManagement from "./views/ComingSoon/DutyRosterManagement";
import RefereeManagement from "./views/TournamentManagement/referee-management";
import PayrollRunWizard from "./views/ComingSoon/PayrollRunWizard";
import PayrollDocuments from "./views/ComingSoon/PayrollDocuments";
import PayrollReports from "./views/Payroll/PayrollReports";
import EOSCenter from "./views/ComingSoon/EOSCenter";
import EmployeeDevelopmentHub from "./views/ComingSoon/EmployeeDevelopmentHub";
import DevelopmentObjectivesCreator from "./views/ComingSoon/DevelopmentObjectivesCreator";
import DevelopmentObjectiveDetail from "./views/ComingSoon/DevelopmentObjectiveDetail";
import TeamDevelopmentDashboard from "./views/ComingSoon/TeamDevelopmentDashboard";
import CheckInsHub from "./views/PerformanceManagement/CheckInsHub";
import PerformanceSnapshot from "./views/PerformanceSnapshot";
import BehaviorValues from "./views/ComingSoon/BehaviorValues";
import CoreHRSettings from "./views/ComingSoon/CoreHRSettings";
import TaskBoard from "./views/ComingSoon/TaskBoard";
import ProjectsList from "./views/ComingSoon/ProjectsList";
import MarketplaceHub from "./views/ComingSoon/MarketplaceHub";
import BulkUploadPage from "./views/General/BulkUpload";
import AssetsPage from "./views/General/Assets";
const HRENPSAnalyticsDashboard = withLazy(() => import("./views/PerformanceManagement/ENPSAnalytics"));
import MyLeaves from "./views/Attendance/MyLeaves";
import LeaveApprovals from "./views/Attendance/LeaveApprovals";
import ShiftSwaps from "./views/Attendance/ShiftSwaps";
import DelegationManagement from "./views/Attendance/DelegationManagement";
import TeamCalendar from "./views/Attendance/TeamCalendar";
import ReturnToWork from "./views/Attendance/ReturnToWork";
import CompOffCredits from "./views/Attendance/CompOffCredits";
import ShortLeave from "./views/Attendance/ShortLeave";
import Encashment from "./views/Attendance/Encashment";
import RecurringLeave from "./views/Attendance/RecurringLeave";
// RestrictedHolidays is EMBEDDED in HolidaysLocationsTab (Leave Settings → Holidays tab) for HR, and
// ALSO mounted standalone at /restricted-holidays — the same component renders the employee's own
// selection surface (Select/Deselect), which is what the RestrictedHoliday pending action needs.
import RestrictedHolidays from "./views/Attendance/RestrictedHolidays";
import StatutoryLeaveTemplates from "./views/Attendance/StatutoryLeaveTemplates";
import YearEndProcessing from "./views/Attendance/YearEndProcessing";
const LeaveStatements = withLazy(() => import("./views/Attendance/LeaveStatements"));
const LeaveBalances = withLazy(() => import("./views/Attendance/LeaveBalances"));
import AbsenceAnalytics from "./views/Attendance/AbsenceAnalytics";
import LeaveSettings from "./views/Settings/Attendance/LeaveSettings";

// Public Profile pages
import ProfileSettings from "./views/ProfileSettings";
import ProfileConfigurationCenter from "./views/ProfileConfigurationCenter";
import HRAdminPublicProfileSettings from "./views/HRAdminPublicProfileSettings";
import PublicProfileDisplay from "./views/PublicProfileDisplay";

// Performance Management imports
import Recognition from "./views/PerformanceManagement/Recognition"
import Reflections from "./views/PerformanceManagement/Reflections"
import Goals from "./views/PerformanceManagement/Goals"
import ESPN from "./views/PerformanceManagement/ESPN"
import TeamHub from "./views/PerformanceManagement/TeamHub"
const InsightsDashboard = withLazy(() => import("./views/PerformanceManagement/InsightsDashboard"))
import ImpactDashboard from "./views/PerformanceManagement/ImpactDashboard"
import MissionStatements from "./views/PerformanceManagement/MissionStatements"
// Lazy, matching its sibling `InsightsDashboard` above: this page now pulls in Recharts, the twelve
// InsightsDashboard panels and the four employee panels, and only a subset of roles can reach it.
const AIImpactDashboard = withLazy(() => import("./views/PerformanceManagement/AIImpactDashboard"))
import TeamReflections from "./views/PerformanceManagement/ManagerResponse"
const Mentorship = withLazy(() => import("./views/PerformanceManagement/Mentorship"));
import UserPayroll from "./views/Payroll/TablePayroll";
const DashboardPayroll = withLazy(() => import("./views/Payroll/DashboardPayroll"));
import Claims from "./views/Payroll/Claims"
import ClaimDetails from "./views/Payroll/Claims/ClaimDetails"
import MyClaims from "./views/Payroll/MyClaims"
import Payslips from "./views/Payroll/Payslips";
import ComplianceCenter from "./views/Payroll/ComplianceCenter";
import WpsCenter from "./views/Payroll/WpsCenter";
import PensionCenter from "./views/Payroll/PensionCenter";

import Currency from "./views/Settings/General/currency";
import GeneralDetails from "./views/Settings/General/generalDetails";
import Entities from "./views/Settings/General/entities";
import AttachmentTypes from "./views/Settings/General/attachmentTypes";
import DateFormatConfiguration from "./views/Settings/General/DateFormatConfiguration";
import ExternalIntegrations from "./views/Settings/General/ExternalIntegrations";

import GrossPaysConfig from "./views/Settings/Payroll/GrossPaysSettings";
import ClaimsSettings from "./views/Settings/Payroll/ClaimsSettings";
import CompanyVariablePayTypesSettings from "./views/Settings/Payroll/CompanyVariablePayTypesSettings";
import CompanyBenefitTypesSettings from "./views/Settings/Payroll/BenefitsSettings";
import EndOfServiceSettings from "./views/Settings/Payroll/EndOfService";
import AttendancePaySettings from "./views/Settings/Payroll/AttendancePaySettings";
import HourlyPaySettings from "./views/Settings/Payroll/HourlyPaySettings";
import PayslipsSettings from "./views/Settings/Payroll/Payslips";
import ComplianceSettings from "./views/Settings/Payroll/ComplianceSettings";

// Billing portal (CO-05). Lazy: only HR/Admin ever reach it, and it pulls in the
// whole billing API surface plus four dialogs, so it has no business sitting in
// the bundle every employee downloads.
const BillingIndex = withLazy(() => import("./views/Settings/Billing/BillingIndex"));
const BillingCheckoutReturn = withLazy(() => import("./views/Settings/Billing/CheckoutReturn"));

import TermsAndPoliciesSetup from "./views/Settings/Company/TermsAndPoliciesSetup";
import MyOnboardingPage from "./views/Onboarding/MyOnboardingPage";
import TermsAcceptanceView from "./views/Onboarding/TermsAcceptanceView";
import LifecycleSettings from "./views/Settings/Employees/LifecycleSettings";
import AssetManagementSettings from "./views/Settings/CoreHR/AssetManagement";
import TeamRoleManagementSettings from "./views/Settings/CoreHR/TeamRoleManagement";

import RecognitionPrivacySettings from "./views/Settings/PreformanceManagement/RecognitionPrivacy";
import RecognitionValues from "./views/Settings/PreformanceManagement/RecognitionValues";

import Personal from "./views/UserProfile/Personal"
import Payroll from "./views/UserProfile/UserPayroll/ActivePayrollMonth";
import Adjustments from "./views/UserProfile/UserPayroll/Adjustments";
// import WorkExpenses from "./components/UserProfile/UserPayroll/WorkExpenses";
import PayHistory from "./views/UserProfile/UserPayroll/PayHistory";
import SalaryConfiguration from "./views/UserProfile/UserPayroll/SalaryConfiguration";
import EndOfService from "./views/UserProfile/UserPayroll/EndOfService";
import FinalSettlement from "./views/UserProfile/UserPayroll/FinalSettlement";
import AttendanceRules from "./views/UserProfile/Attendance/Rules";
import AttendanceMap from "./views/UserProfile/Attendance/WorkplaceMap";
import UserProfileLeaves from "./views/UserProfile/Leaves";
import VariablePay from "./views/UserProfile/UserPayroll/VariablePay";
import UserBenefits from "./views/UserProfile/UserPayroll/Benefits";
import Skills from "./views/UserProfile/Skills";
import Documents from "./views/UserProfile/Documents";
import ProfileAssets from "./views/UserProfile/Assets";
import ProfileBusinessRoles from "./views/UserProfile/BusinessRoles";
import ProfileDependents from "./views/UserProfile/Dependents";
import UserProfilePayslips from "./views/UserProfile/UserPayroll/Payslips";
import ProfileHourlyPay from "./views/UserProfile/UserPayroll/HourlyPay";

import AddEditCompany from "./views/Settings/Companies/AddEditCompany";
import SubscriptionsIndex from "./views/Settings/Subscriptions/SubscriptionsIndex";
import RefundQueueIndex from "./views/Settings/Subscriptions/RefundQueueIndex";
const BatchAddingUsers = withLazy(() => import("./views/Settings/Companies/BatchAddingUsers"));
const WelcomeEmails = withLazy(() => import("./views/EmployeesCycle/General/WelcomeEmails"));
import OperatorCompanyRules from "./views/Settings/Companies/OperatorCompanyRules";
import OperatorLeaveConfiguration, { LegacyLeavesManagementRedirect } from "./views/Settings/Companies/OperatorLeaveConfiguration";
import OperatorEngagementJobs from "./views/Settings/Companies/OperatorEngagementJobs";
import AppVersionIndex from "./views/Settings/AppVersion/AppVersionIndex";
import WorkplaceMap from "./views/Settings/Attendance/workplaceMap";
import Weekschedule from "./views/Settings/Attendance/workscheduleconfig";
import FaceRecognitionSettings from "./views/Settings/Attendance/FaceRecognitionSettings";
import WialonSettings from "./views/Settings/Attendance/WialonSettings";
import FileUploadSettings from "./views/Settings/General/FileUploadSettings";
import OcrSettings from "./views/Settings/General/OcrSettings";
import BlobMigration from "./views/Settings/BlobMigration";
import Projects from "./views/Settings/ProjectManagement/Projects";
import SystemEmails from "./views/Settings/General/systemEmails";
// CO-322 — append-only security audit log. HR/Admin see their company; Operator sees platform events (server-enforced).
import SecurityAudit from "./views/Settings/General/SecurityAudit";
import AISettings from "./views/Settings/AI/AISettings";
// 🔴 OWNER DECISION E12 (2026-08-20) — the Operator console for business configuration that has
// LEFT appsettings.json. Platform default + per-company override, both editable at runtime.
import PlatformSettings from "./views/Settings/Platform/PlatformSettings";
// CO-355 — HR/Admin READ-ONLY view of the company's daily AI allowance. The quota itself is set
// by the Operator on /settings/ai-settings; this route has no write path at all.
import AiQuotaOverview from "./views/Settings/AI/AiQuotaOverview";
import ModuleConfig from "./views/Settings/ModuleConfig";
import UserNotificationPreferences from "./views/Settings/Notifications/UserNotificationPreferences";
import AdminNotificationManagement from "./views/Settings/Notifications/AdminNotificationManagement";
import Unsubscribe from "./views/General/unsubscribe";
import PayrollPayslipsSettings from "./views/Settings/Payroll/Payslips";

// Per-module brochures (Phase 6). The router switches on `:moduleSlug` and
// renders the matching brochure component, falling back to a "coming soon"
// card for slugs that haven't shipped their dedicated page yet.
import BrochureRouter from "./views/Brochures/BrochureRouter";

/*
  Seeding is the ONE page whose buttons wipe a tenant. It is lazy on purpose, not
  for weight: paired with `DevToolsRoute` the chunk is never requested in a
  production build, so the destructive page's code stays out of the main bundle
  instead of merely being unreachable inside it.
*/
const Seeding = withLazy(() => import("./views/Settings/Development/Seeding"));

// Recruitment module (UI-only, ported from mowazaf-rocketnew)
const RecruitmentDashboard = withLazy(() => import("./views/Recruitment/recruitment-dashboard"));
const RecruitmentSettings = withLazy(() => import("./views/Recruitment/recruitment-settings"));
const JobOpeningsManagement = withLazy(() => import("./views/Recruitment/job-openings-management"));
const CandidatesManagementCenter = withLazy(() => import("./views/Recruitment/candidates-management-center"));
const HiringPipeline = withLazy(() => import("./views/Recruitment/hiring-pipeline"));
const InterviewsManagement = withLazy(() => import("./views/Recruitment/interviews-management"));
const OffersManagement = withLazy(() => import("./views/Recruitment/offers-management"));

interface IProps {
  isLoggedIn: boolean;
  fullName: string;
  role: string;
  locale: string;
  companyName: string;
  companyLogo: string | null;
  companyLocales: string[];
  companyPrimaryColor: string | null;
  currentStatus: string | null;
  usercurrentCustomStatusText: string | null;
  prevUrl: string;
  userId: string;
  imageUrl: string | null;
  anyUserWithVariableScheduleRule: boolean;
  token: string;
  isManager: boolean;
  rtl: boolean;
  // CO-239 — environment-level billing master switch (`Billing:Enabled`), delivered on the company
  // object by GetUserInfo. Optional + fail-closed: see the /settings/billing routes below.
  billingEnabled?: boolean;
}

const _App = (props: IProps) => {
  const { t, i18n } = useTranslation();
  const [availabilityStatusDialogOpen, setAvailabilityStatusDialogOpen] = useState(false);
  const complianceCountry = useComplianceCountry();
  const {
    isLoggedIn,
    locale,
    companyPrimaryColor,
    rtl
  } = props;

  // CO-239 — /settings/billing is gated on the ENVIRONMENT's billing master switch as well as on role.
  // Fail closed on `undefined`: a redux-persist snapshot written before the field existed carries no
  // value, and on deploy day that stale snapshot belongs to exactly the legacy perpetual tenant this is
  // meant to keep off the page. Mirrors the nav gate in useFilteredNavigation — keep the two in step.
  const billingRouteRoles = props.billingEnabled === true ? SETTINGS_ROLES : NO_ROLES;

  // Keep CSS variables in sync with company primary color without overriding pre-set var on initial mount
  useEffect(() => {
    import('./utils').then(({ generatePalette, setCSSColorVariables }) => {
      const next = companyPrimaryColor ?? '#304EA1';
      try {
        const currentVar = typeof document !== 'undefined'
          ? getComputedStyle(document.documentElement).getPropertyValue('--mowazaf-primary').trim()
          : '';
        // If initial Redux default is present but CSS var is already set (from persisted color), do not override
        const isInitialDefault = (companyPrimaryColor || '').toLowerCase() === '#304EA1';
        if (isInitialDefault && currentVar) return;
        if (currentVar && currentVar.toLowerCase() === (next || '').toLowerCase()) return;
      } catch { }
      const palette = generatePalette(next);
      setCSSColorVariables(palette);
    }).catch(() => { });
  }, [companyPrimaryColor]);

  const dispatch = useDispatch();

  // Keep i18n language in sync with persisted Redux locale (after rehydration and on changes)
  useEffect(() => {
    if (!locale) return;
    // 🔴 Do NOT re-issue a change that is already in flight. `LanguageSwitch` calls
    // `i18n.changeLanguage(lang)` AND dispatches SET_LOCALE; i18next only updates
    // `i18n.language` once `loadResources` has resolved for ~13 namespaces, so this
    // effect used to see a still-stale `i18n.language` and fire a SECOND
    // `changeLanguage` for the same target — doubling the namespace fetch storm on
    // the exact path being timed. `isLanguageChangingTo` (i18next 22.5.1,
    // i18next.js:2271/2284/2288) is the in-flight target, cleared on completion.
    const inFlight = (i18n as unknown as { isLanguageChangingTo?: string }).isLanguageChangingTo;
    if (i18n.language !== locale && inFlight !== locale) {
      i18n.changeLanguage(locale);
    }
    try {
      // Update moment locale as well
      moment.locale(locale);
      if (constants.rtlLangs.indexOf(locale) > -1 && locale === 'ar') {
        // Ensure Arabic numerals formatting stays consistent if needed
        moment.defineLocale('ar-arNum', {
          parentLocale: 'ar',
          preparse: function (str: string) { return str; },
          postformat: function (str: string) { return str; }
        });
        moment.locale('ar-arNum');
      }
    } catch { }
  }, [locale, i18n]);

  // Keep HTML document direction in sync with the active i18n language.
  // The Redux `rtl` flag only exists for authenticated users; anonymous visitors
  // hitting /p/{slug} and toggling the in-page language switcher would otherwise
  // get Arabic strings with an LTR layout. Listening to i18n itself covers both.
  useEffect(() => {
    // Both `lang` and `dir` are owned by `utils/documentLocale.applyDocumentLocale` — the same
    // function the pre-mount boot block in main.tsx and LanguageSwitch's click handler call. The
    // static shell ships lang="en" (and the SSR /p/{slug} handler rewrites it server-side), so a
    // locked-language public profile (ar/fr) — or any client language switch — needs this to stop
    // announcing the wrong language to screen readers and crawlers.
    const applyDir = (lang: string | undefined) => { applyDocumentLocale(lang); };

    // 🔴 Redux `locale` updates SYNCHRONOUSLY on a language click; `i18n.language`
    // does not (i18next only swaps it after `loadResources` resolves, then emits
    // `languageChanged`). Applying dir from `i18n.language` alone therefore left the
    // document in the OLD direction for the whole width of that namespace fetch.
    // Prefer redux while authenticated — and only while authenticated: the anonymous
    // /p/{slug} surface locks its language through `i18n.changeLanguage` and never
    // dispatches SET_LOCALE, so its redux locale stays at the "en" default and must
    // not be allowed to win. `languageChanged` still re-applies for both paths.
    applyDir(isLoggedIn ? (locale || i18n.language) : i18n.language);
    const onLanguageChanged = (lng: string) => applyDir(lng);
    i18n.on('languageChanged', onLanguageChanged);
    return () => {
      i18n.off('languageChanged', onLanguageChanged);
    };
  }, [i18n, rtl, locale, isLoggedIn]);

  const closeAvailabilityStatusDialog = () => {
    setAvailabilityStatusDialogOpen(false);
  };

  return (
    <>
          <I18nextProvider i18n={i18n}>
            {/* Global dialogs that can appear anywhere */}
            <CurrentStatus
              semibold={false}
              asDialog={true}
              dialogIsOpen={availabilityStatusDialogOpen}
              closeAvailabilityStatusDialog={closeAvailabilityStatusDialog}
            />

            <div className="h-screen flex flex-col overflow-hidden">
            <Routes>
              {/* ===== Public routes (no authentication, no MasterLayout) ===== */}
              <Route path="/logincallback" element={<Callback />} />
              <Route path="/silent-renew" element={<SilentRenewCallback />} />
              <Route path="/logoutcallback" element={<LogoutCallback />} />
              <Route path="/signuptrial" element={<Navigate to="/signup" replace />} />
              <Route path="/signup-trial" element={<Navigate to="/signup" replace />} />
              <Route path="/signup" element={<SignupFree />} />
              <Route path="/privacypolicy" element={<PrivacyPolicy />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/widget" element={<Widget />} />
              <Route path="/p/:slug" element={<ErrorBoundary><PublicProfileDisplay /></ErrorBoundary>} />

              {/* ===== Authenticated routes with MasterLayout ===== */}
              <Route element={<PrivateRoute />}>
                <Route element={<OnboardingGate />}>
                <Route element={<MasterLayout />}>
                  {/* Home / Dashboard */}
                  <Route path="/" element={<HomeRoute />} />
                  <Route path="/apps" element={<CompanyDashboard />} />

                  {/* Pending Actions — every user can have items (approvals + self-actions);
                      the aggregator scopes per caller, so no role guard here. */}
                  <Route path="/pending-actions" element={<PendingActions />} />

                  {/* AI Implementation Consultant — HR/Admin only (configures the system via chat) */}
                  <Route path="/ai-consultant" element={<RoleProtectedRoute allowedRoles={HR_AND_ADMIN_ROLES}><AIConsultant /></RoleProtectedRoute>} />
                  <Route path="/ai-consultant/history" element={<RoleProtectedRoute allowedRoles={HR_AND_ADMIN_ROLES}><AIConsultantHistory /></RoleProtectedRoute>} />

                  {/* Notifications */}
                  <Route path="/:company?/notifications" element={<Notifications />} />

                  {/* Attendance */}
                  {/* The leading `:company?` (vestigial — never read) collided with
                      `/brochure/attendance`, sending Free-tier users to the actual
                      Attendance page instead of the brochure. Restrict to `/attendance/...`. */}
                  <Route path="/attendance/:action?/:shiftId?" element={<Attendance />} />
                  <Route path="/attendance/overview/:targetUserId?/:targetUserFullName?" element={<AttendanceOverview />} />
                  <Route path="/attendance/:company?/leavesAndHolidays" element={<Navigate to="/my-leaves" replace />} />
                  <Route path="/attendance/:company?/maps" element={<RoleProtectedRoute allowedRoles={MANAGER_ROLES}><Maps /></RoleProtectedRoute>} />
                  <Route path="/attendance/:company?/requests" element={
                    <RoleProtectedRoute allowedRoles={MANAGER_ROLES}>
                      <Navigate to="/leave-approvals" replace />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/attendance/correction-requests" element={<AttendanceCorrectionRequests />} />
                  <Route path="/attendance/colleagues-availability" element={<Dashboard />} />
                  <Route path="/attendance/dashboard" element={<Navigate to="/attendance/colleagues-availability" replace />} />
                  <Route path="/attendance/team-attendance" element={<RoleProtectedRoute allowedRoles={MANAGER_ROLES}><TeamAttendancePage /></RoleProtectedRoute>} />
                  <Route path="/attendance/work-schedules" element={<RoleProtectedRoute allowedRoles={MANAGER_ROLES}><WorkSchedules /></RoleProtectedRoute>} />
                  <Route path="/attendance/workschedules" element={<Navigate to="/attendance/work-schedules" replace />} />
                  <Route path="/attendance/work-schedules/recurring" element={<RoleProtectedRoute allowedRoles={MANAGER_ROLES}><RecurringSchedules /></RoleProtectedRoute>} />
                  <Route path="/attendance/workschedules/recurring" element={<Navigate to="/attendance/work-schedules/recurring" replace />} />
                  {/* Shift Swaps — any employee can request; the approvals tab self-gates by role inside the page.
                      Route is guarded so it's hidden when the feature isn't enabled for the user/company (UX only;
                      the backend remains the security boundary). */}
                  <Route path="/shift-swaps" element={<ShiftSwapEnabledRoute><ShiftSwaps /></ShiftSwapEnabledRoute>} />

                  {/* Attendance Reports */}
                  <Route path="/attendance/reports" element={
                    <RoleProtectedRoute allowedRoles={MANAGER_ROLES}>
                      <Reports />
                    </RoleProtectedRoute>
                  } />
                  {/* Canonical kebab-case report routes */}
                  <Route path="/attendance/reports/users-statuses/:from/:to/:parameters/:format" element={<UsersStatusesReportExport />} />
                  <Route path="/attendance/reports/clockings/:from/:to/:parameters/:format" element={<ClockingsReportExport />} />
                  <Route path="/attendance/reports/shifts/:from/:to/:parameters/:format" element={<ShiftsReportExport />} />
                  <Route path="/attendance/reports/overtime/:from/:to/:parameters/:format" element={<OvertimeReportExport />} />
                  <Route path="/attendance/reports/clockings" element={<RoleProtectedRoute allowedRoles={MANAGER_ROLES}><ClockingsReport /></RoleProtectedRoute>} />
                  <Route path="/attendance/reports/shifts" element={<RoleProtectedRoute allowedRoles={MANAGER_ROLES}><ShiftsReport /></RoleProtectedRoute>} />
                  <Route path="/attendance/reports/work-sessions" element={<RoleProtectedRoute allowedRoles={MANAGER_ROLES}><WorkSessionsReport /></RoleProtectedRoute>} />
                  <Route path="/attendance/reports/overtime" element={<RoleProtectedRoute allowedRoles={MANAGER_ROLES}><OvertimeReport /></RoleProtectedRoute>} />
                  <Route path="/attendance/reports/out-of-bounds" element={<RoleProtectedRoute allowedRoles={MANAGER_ROLES}><OutOfBoundsReport /></RoleProtectedRoute>} />
                  {/* The two leave reports (SPEC docs/reports-revamp/SPEC.md §9, §10). All seven report tab
                      routes carry MANAGER_ROLES like the hub (SPEC-attendance-remaining §4.3); the server
                      still scopes every roster to CompanyId ∩ GetAuthorizedUserIdsAsync. */}
                  <Route path="/attendance/reports/leaves" element={<RoleProtectedRoute allowedRoles={MANAGER_ROLES}><LeavesReport /></RoleProtectedRoute>} />
                  <Route path="/attendance/reports/leave-balance" element={<RoleProtectedRoute allowedRoles={MANAGER_ROLES}><LeaveBalanceReport /></RoleProtectedRoute>} />

                  {/* Attendance › Overtime (CO-583). OPEN TO EVERY ROLE, and deliberately NOT
                      wrapped in RoleProtectedRoute any more.

                      The old CORE_HR_ROLES guard was written when this route had one mode (the
                      approval queue) and bouncing a Clocking employee only cost them an empty page.
                      It now has two: an approver gets the queue, and everyone else gets their OWN
                      overtime — the shifts the company made them ask about, and what came of the
                      asking. Withholding that from the person whose wages it is was the defect.

                      🔴 NOTHING IS WIDENED BY REMOVING THE GUARD. The route only ever rendered a
                      client; the authority is `GET api/OvertimeApprovals/My`, which already scopes a
                      non-privileged caller to SELF and ships `canDecide: false` on every row, and
                      `POST attendance/OvertimeShiftApproval`, which still 403s a non-approver. A
                      Clocking user reaching this page reads exactly what the server would have sent
                      them anyway. The Free-tier / restricted-write locks are unchanged: they live in
                      TierEnforcementMiddleware on the server and useWriteAccess on the client, not
                      in this guard. */}
                  <Route path="/attendance/overtime-approvals" element={<OvertimeApprovals />} />
                  {/* Hourly pay (docs/hourly-pay-spec.md §6, §7.2, §9 deep links). Role first, feature
                      second: the queue is for approvers in a company with hourly pay ON; My shifts is for
                      an Hourly employee in such a company (GetUserInfo payBasis + hourlyPayEnabled). Both
                      guards are UX only — ShiftPayApprovals/Queue and /My scope every caller server-side. */}
                  <Route path="/attendance/shift-approvals" element={<RoleProtectedRoute allowedRoles={SHIFT_APPROVER_ROLES}><HourlyPayRoute require="company"><ShiftApprovals /></HourlyPayRoute></RoleProtectedRoute>} />
                  <Route path="/attendance/my-shifts" element={<RoleProtectedRoute allowedRoles={EMPLOYEE_SELF_SERVICE_ROLES}><HourlyPayRoute require="hourlyEmployee"><MyShifts /></HourlyPayRoute></RoleProtectedRoute>} />
                  {/* Back-compat: old PascalCase report routes (Navigate can't interpolate multi-param URLs) */}
                  <Route path="/attendance/reports/UsersStatuses/:from/:to/:parameters/:format" element={<UsersStatusesReportExport />} />
                  <Route path="/attendance/reports/Clockings/:from/:to/:parameters/:format" element={<ClockingsReportExport />} />
                  <Route path="/attendance/reports/Shifts/:from/:to/:parameters/:format" element={<ShiftsReportExport />} />
                  <Route path="/attendance/reports/Overtime/:from/:to/:parameters/:format" element={<OvertimeReportExport />} />
                  <Route path="/attendance/reports/Clockings" element={<Navigate to="/attendance/reports/clockings" replace />} />
                  <Route path="/attendance/reports/Shifts" element={<Navigate to="/attendance/reports/shifts" replace />} />
                  <Route path="/attendance/reports/WorkSessions" element={<Navigate to="/attendance/reports/work-sessions" replace />} />
                  <Route path="/attendance/reports/Overtime" element={<Navigate to="/attendance/reports/overtime" replace />} />

                  {/* Users — Hr/Admin batch importer (company-scoped to their own company inside the component) */}
                  <Route path="/:company?/users/add-multiple" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><BatchAddingUsers /></RoleProtectedRoute>} />
                  <Route path="/:company?/users/addmultiple" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><BatchAddingUsers /></RoleProtectedRoute>} />
                  <Route path="/:company?/users/add" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><AddUser /></RoleProtectedRoute>} />
                  <Route path="/:company?/users/send-notification" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><OnDemandNotification /></RoleProtectedRoute>} />
                  <Route path="/:company?/users/sendNotification" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><OnDemandNotification /></RoleProtectedRoute>} />
                  <Route path="/:company?/users/:id/leavesAndHolidays" element={<Navigate to="/my-leaves" replace />} />

                  {/* Payroll — HR-only dashboards; claims/myclaims/payslips reachable by
                      employees for their own data and are scoped server-side. */}
                  <Route path="/payroll/table" element={
                    <RoleProtectedRoute allowedRoles={HR_ONLY_ROLES}>
                      <UserPayroll />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/payroll/dashboard" element={
                    <RoleProtectedRoute allowedRoles={HR_ONLY_ROLES}>
                      <DashboardPayroll />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/payroll/claims/:claimId" element={<ClaimDetails />} />
                  <Route path="/payroll/claims" element={
                    <RoleProtectedRoute allowedRoles={HR_ONLY_ROLES}>
                      <Claims />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/payroll/my-claims" element={<MyClaims />} />
                  <Route path="/payroll/myclaims" element={<Navigate to="/payroll/my-claims" replace />} />
                  <Route path="/payroll/payslips" element={
                    <RoleProtectedRoute allowedRoles={HR_ONLY_ROLES}>
                      <Payslips />
                    </RoleProtectedRoute>
                  } />
                  {(complianceCountry === 'UAE' || complianceCountry === 'KSA') && (
                    <Route path="/payroll/compliance-center" element={
                      <RoleProtectedRoute allowedRoles={HR_ONLY_ROLES}>
                        <ComplianceCenter />
                      </RoleProtectedRoute>
                    } />
                  )}
                  {(complianceCountry === 'UAE' || complianceCountry === 'KSA') && (
                    <Route path="/payroll/wps-center" element={
                      <RoleProtectedRoute allowedRoles={HR_ONLY_ROLES}>
                        <WpsCenter />
                      </RoleProtectedRoute>
                    } />
                  )}
                  {(complianceCountry === 'UAE' || complianceCountry === 'KSA') && (
                    <Route path="/payroll/pension" element={
                      <RoleProtectedRoute allowedRoles={HR_ONLY_ROLES}>
                        <PensionCenter />
                      </RoleProtectedRoute>
                    } />
                  )}

                  {/* Employees Cycle */}
                  <Route path="/core-hr/directory" element={<RoleProtectedRoute allowedRoles={CORE_HR_ROLES}><EmployeesGeneral /></RoleProtectedRoute>} />
                  {/* Hr/Admin welcome-emails management (fixed path; component pins to their own company via redux) */}
                  <Route path="/core-hr/welcome-emails" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><WelcomeEmails /></RoleProtectedRoute>} />
                  <Route path="/core-hr/org-structure" element={<RoleProtectedRoute allowedRoles={CORE_HR_ROLES}><OrganizationalStructure /></RoleProtectedRoute>} />
                  <Route path="/core-hr/grouping" element={<RoleProtectedRoute allowedRoles={HR_AND_ADMIN_ROLES}><EmployeesGrouping /></RoleProtectedRoute>} />
                  <Route path="/core-hr/SetUp" element={<RoleProtectedRoute allowedRoles={CORE_HR_ROLES}><SetUp /></RoleProtectedRoute>} />
                  <Route path="/core-hr/skills-overview" element={<RoleProtectedRoute allowedRoles={CORE_HR_ROLES}><SkillsOverview /></RoleProtectedRoute>} />

                  {/* Profile routes - NOW INSIDE MasterLayout (sidebar + topbar persist) */}
                  <Route path="/profile/:id/personal" element={<Personal />} />
                  <Route path="/profile/:id/skills" element={<Skills />} />
                  <Route path="/profile/:id/payroll/active-payroll-month" element={<ProfileAccessGuard othersAllowedRoles={HR_ONLY_ROLES}><Payroll /></ProfileAccessGuard>} />
                  <Route path="/profile/:id/payroll/variable-pay" element={<ProfileAccessGuard othersAllowedRoles={HR_ONLY_ROLES}><VariablePay /></ProfileAccessGuard>} />
                  <Route path="/profile/:id/payroll/adjustments" element={<ProfileAccessGuard othersAllowedRoles={HR_ONLY_ROLES}><Adjustments /></ProfileAccessGuard>} />
                  <Route path="/profile/:id/payroll/benefits" element={<ProfileAccessGuard othersAllowedRoles={HR_ONLY_ROLES}><UserBenefits /></ProfileAccessGuard>} />
                  <Route path="/profile/:id/payroll/pay-history" element={<ProfileAccessGuard othersAllowedRoles={HR_ONLY_ROLES}><PayHistory /></ProfileAccessGuard>} />
                  <Route path="/profile/:id/payroll/salary-configuration" element={<ProfileAccessGuard othersAllowedRoles={HR_ONLY_ROLES}><SalaryConfiguration /></ProfileAccessGuard>} />
                  <Route path="/profile/:id/payroll/end-of-service" element={<ProfileAccessGuard othersAllowedRoles={HR_ONLY_ROLES}><EndOfService /></ProfileAccessGuard>} />
                  <Route path="/profile/:id/payroll/final-settlement" element={<ProfileAccessGuard othersAllowedRoles={HR_ONLY_ROLES}><FinalSettlement /></ProfileAccessGuard>} />
                  <Route path="/profile/:id/attendance/rules" element={<AttendanceRules />} />
                  <Route path="/profile/:id/leaves" element={<UserProfileLeaves />} />
                  <Route path="/profile/:id/map" element={<AttendanceMap />} />
                  {/* Documents also hosts the self-service "On Demand Documents" section (own profile only). */}
                  <Route path="/profile/:id/documents" element={<ProfileAccessGuard othersAllowedRoles={HR_ONLY_ROLES}><Documents /></ProfileAccessGuard>} />
                  {/* CO-364 — ASSETS_PAGE_ROLES, not HR_ONLY_ROLES. The backend's GetEmployeeAssets now
                      admits the AssetManagement tag (it already served every one of those fields to the
                      same principal via GetAssets and GetAssetHistory), so an HR-only route guard here
                      would leave the endpoint reachable while the SPA refused to route to it. */}
                  <Route path="/profile/:id/assets" element={<ProfileAccessGuard othersAllowedRoles={ASSETS_PAGE_ROLES}><ProfileAssets /></ProfileAccessGuard>} />
                  <Route path="/profile/:id/business-roles" element={<ProfileBusinessRoles />} />
                  {/* Back-compat: old camelCase profile sub-routes serve the same component (Navigate can't interpolate :id) */}
                  <Route path="/profile/:id/payroll/activePayrollMonth" element={<ProfileAccessGuard othersAllowedRoles={HR_ONLY_ROLES}><Payroll /></ProfileAccessGuard>} />
                  <Route path="/profile/:id/payroll/variablePay" element={<ProfileAccessGuard othersAllowedRoles={HR_ONLY_ROLES}><VariablePay /></ProfileAccessGuard>} />
                  <Route path="/profile/:id/payroll/payhistory" element={<ProfileAccessGuard othersAllowedRoles={HR_ONLY_ROLES}><PayHistory /></ProfileAccessGuard>} />
                  <Route path="/profile/:id/payroll/salaryconfiguration" element={<ProfileAccessGuard othersAllowedRoles={HR_ONLY_ROLES}><SalaryConfiguration /></ProfileAccessGuard>} />
                  <Route path="/profile/:id/payroll/endOfService" element={<ProfileAccessGuard othersAllowedRoles={HR_ONLY_ROLES}><EndOfService /></ProfileAccessGuard>} />
                  <Route path="/profile/:id/payroll/finalSettlement" element={<ProfileAccessGuard othersAllowedRoles={HR_ONLY_ROLES}><FinalSettlement /></ProfileAccessGuard>} />
                  <Route path="/profile/:id/businessRoles" element={<ProfileBusinessRoles />} />
                  <Route path="/profile/:id/dependents" element={<ProfileDependents />} />
                  <Route path="/profile/:id/payroll/payslips" element={<ProfileAccessGuard othersAllowedRoles={HR_ONLY_ROLES}><UserProfilePayslips /></ProfileAccessGuard>} />
                  {/* Hourly pay: pay basis + rate history. Salary data — HR ONLY even on one's own profile,
                      because api/UserHourlyRate is HR-only (a self-view would only ever 403). */}
                  <Route path="/profile/:id/payroll/hourly-pay" element={<RoleProtectedRoute allowedRoles={HR_ONLY_ROLES}><ProfileHourlyPay /></RoleProtectedRoute>} />

                  {/* Settings */}
                  {/* Billing (CO-05). SETTINGS_ROLES = ['Hr','Admin'] — deliberately NOT
                      OPERATOR_ROLES: billing is company data, and the Role Model keeps
                      Operator off every company-data gate. A plain employee gets no route
                      and no nav entry, so billing does not exist for them at all.

                      CO-239: `billingRouteRoles` collapses to NO_ROLES while the environment's
                      billing master switch is off, so typing the URL cannot reach the page the
                      nav item is being hidden from. The return route is gated identically — it
                      only ever completes a checkout that could not have been started. */}
                  <Route path="/settings/billing" element={<RoleProtectedRoute allowedRoles={billingRouteRoles}><BillingIndex /></RoleProtectedRoute>} />
                  <Route path="/settings/billing/return" element={<RoleProtectedRoute allowedRoles={billingRouteRoles}><BillingCheckoutReturn /></RoleProtectedRoute>} />
                  <Route path="/settings/general-details" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><GeneralDetails /></RoleProtectedRoute>} />
                  <Route path="/settings/generalDetails" element={<Navigate to="/settings/general-details" replace />} />
                  <Route path="/settings/entities" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><Entities /></RoleProtectedRoute>} />
                  <Route path="/settings/currency-and-banks" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><Currency /></RoleProtectedRoute>} />
                  <Route path="/settings/currencyAndBanks" element={<Navigate to="/settings/currency-and-banks" replace />} />
                  {/* Superseded by /departments-locations (owner, 2026-08-19). Both paths REDIRECT rather
                      than 404 so existing bookmarks, in-app links and E2E navigations keep working.
                      Safe on roles: the old gate SETTINGS_ROLES [Hr, Admin] is a strict SUBSET of the
                      destination CORE_HR_ROLES [Manager, Hr, Chief, Admin], so nobody who could reach the
                      old page lands on a 403 - and a Clocking user is still bounced by the destination own
                      guard, which is what route-guards.clocking.spec.ts asserts. */}
                  <Route path="/settings/branches-and-departments" element={<Navigate to="/departments-locations" replace />} />
                  <Route path="/settings/branchesAndDepartments" element={<Navigate to="/departments-locations" replace />} />
                  <Route path="/settings/payroll/gross-pays" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><GrossPaysConfig /></RoleProtectedRoute>} />
                  <Route path="/settings/payroll/grosspays" element={<Navigate to="/settings/payroll/gross-pays" replace />} />
                  <Route path="/settings/payroll/claims" element={<RoleProtectedRoute allowedRoles={HR_ONLY_ROLES}><ClaimsSettings /></RoleProtectedRoute>} />
                  <Route path="/settings/payroll/variable-pays" element={<RoleProtectedRoute allowedRoles={HR_ONLY_ROLES}><CompanyVariablePayTypesSettings /></RoleProtectedRoute>} />
                  <Route path="/settings/payroll/variablepays" element={<Navigate to="/settings/payroll/variable-pays" replace />} />
                  <Route path="/settings/payroll/benefits" element={<RoleProtectedRoute allowedRoles={HR_ONLY_ROLES}><CompanyBenefitTypesSettings /></RoleProtectedRoute>} />
                  <Route path="/settings/payroll/end-of-service" element={<RoleProtectedRoute allowedRoles={HR_ONLY_ROLES}><EndOfServiceSettings /></RoleProtectedRoute>} />
                  <Route path="/settings/payroll/endOfService" element={<Navigate to="/settings/payroll/end-of-service" replace />} />
                  <Route path="/settings/payroll/payslips" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><PayslipsSettings /></RoleProtectedRoute>} />
                  {/* CO-304 — overtime/attendance pay settings. DELIBERATELY NOT wrapped in the
                      complianceCountry condition below: that gate is why these settings were
                      unreachable for the ~88% of tenants with no compliance country. */}
                  <Route path="/settings/payroll/attendance" element={<RoleProtectedRoute allowedRoles={HR_ONLY_ROLES}><AttendancePaySettings /></RoleProtectedRoute>} />
                  {/* Hourly pay settings (spec §3.3). HR_ONLY like its sibling above — it sets pay
                      multipliers. No hourly-pay feature guard: this is where the switch is turned on. */}
                  <Route path="/settings/payroll/hourly-pay" element={<RoleProtectedRoute allowedRoles={HR_ONLY_ROLES}><HourlyPaySettings /></RoleProtectedRoute>} />
                  {(complianceCountry === 'UAE' || complianceCountry === 'KSA' || props.role === 'Operator') && (
                    <Route path="/settings/payroll/compliance" element={
                      <RoleProtectedRoute allowedRoles={HR_AND_OPERATOR_ROLES}>
                        <ComplianceSettings />
                      </RoleProtectedRoute>
                    } />
                  )}
                  <Route path="/settings/company/terms-and-policies" element={
                    <RoleProtectedRoute allowedRoles={HR_ONLY_ROLES}>
                      <TermsAndPoliciesSetup />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/onboarding/complete" element={<MyOnboardingPage />} />
                  <Route path="/terms-and-policies/accept" element={<TermsAcceptanceView />} />
                  {/* Exit/termination reasons are managed under Lifecycle Settings → Exit Reasons tab. Old dead mock page removed; redirect legacy paths there. */}
                  <Route path="/settings/termination-reasons" element={<Navigate to="/settings/lifecycle" replace />} />
                  <Route path="/settings/reasonsOfTerminationResignation" element={<Navigate to="/settings/lifecycle" replace />} />
                  <Route path="/settings/lifecycle" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><LifecycleSettings /></RoleProtectedRoute>} />
                  {/* Hr = implicit asset manager (config tabs); Admin is admitted only to administer the
                      AssetManagement tag (assetRoles tab) — the page hides the config tabs from them. */}
                  <Route path="/settings/core-hr/asset-management" element={<RoleProtectedRoute allowedRoles={['Hr', 'AssetManagement', 'Admin']}><AssetManagementSettings /></RoleProtectedRoute>} />
                  <Route path="/settings/core-hr/team-role-management" element={<RoleProtectedRoute allowedRoles={CORE_HR_SETTINGS_ROLES}><TeamRoleManagementSettings /></RoleProtectedRoute>} />
                  {/* Attendance Rules merged into Work Schedule Config (single company-rules page). */}
                  <Route path="/settings/attendance/attendance-rules" element={<Navigate to="/settings/attendance/work-schedule-config" replace />} />
                  <Route path="/settings/attendance/rulesManagement" element={<Navigate to="/settings/attendance/work-schedule-config" replace />} />
                  {/* Legacy alias. Role-aware: Operator reaches the config-only page (owner carve-out
                      2026-07-29), everyone else keeps landing on the HR-only /leave-policies. */}
                  <Route path="/settings/attendance/leavesManagement" element={<LegacyLeavesManagementRedirect />} />
                  {/* The per-country holiday grid (companyHolidays -> holidaysConfig, over HolidayService)
                      was REMOVED 2026-09-09. It was a capability subset of the "Holidays & Locations" tab
                      inside Leave Settings, which adds file import, group all/some/none states and
                      branches, and whose service (LeavePolicyService) is the one the AI consultant tools
                      write through. Two live holiday screens over two different services is what produced
                      the refuted premise recorded in docs/AGENT-MEMORY.md.
                      OWNER DECISION 2026-09-09: /leave-policies stays HR-ONLY, so Admins deliberately lose
                      holiday management rather than the route being widened. Both old paths redirect there
                      so bookmarks resolve; an Admin following one now hits the HR-only guard, which is the
                      intended behaviour change and is in the client notes. */}
                  <Route path="/settings/attendance/holidays" element={<Navigate to="/leave-policies" replace />} />
                  <Route path="/settings/attendance/holidaysManagement" element={<Navigate to="/leave-policies" replace />} />
                  <Route path="/settings/attendance/workplace-map" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><WorkplaceMap /></RoleProtectedRoute>} />
                  <Route path="/settings/attendance/workplaceMap" element={<Navigate to="/settings/attendance/workplace-map" replace />} />
                  <Route path="/settings/attendance/work-schedule-config" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><Weekschedule /></RoleProtectedRoute>} />
                  <Route path="/settings/attendance/workscheduleconfig" element={<Navigate to="/settings/attendance/work-schedule-config" replace />} />
                  <Route path="/settings/attendance/face-recognition" element={
                    <RoleProtectedRoute allowedRoles={OPERATOR_ROLES}>
                      <FaceRecognitionSettings />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/settings/attendance/wialon" element={
                    <RoleProtectedRoute allowedRoles={['Operator']}>
                      <WialonSettings />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/settings/general/file-upload-settings" element={
                    <RoleProtectedRoute allowedRoles={OPERATOR_ROLES}>
                      <FileUploadSettings />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/settings/general/ocr-settings" element={
                    <RoleProtectedRoute allowedRoles={SETTINGS_ROLES}>
                      <OcrSettings />
                    </RoleProtectedRoute>
                  } />
                  {/* UI-only preview; SETTINGS_ROLES mirrors ROLES.HR_AND_ADMIN on its
                      navigationModules entry, so the menu link and the route agree. */}
                  <Route path="/settings/general/chatbot-analysis" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><ChatbotSettingsAnalysis /></RoleProtectedRoute>} />
                  <Route path="/settings/general/external-integrations" element={<RoleProtectedRoute allowedRoles={INTEGRATIONS_ROLES}><ExternalIntegrations /></RoleProtectedRoute>} />
                  <Route path="/settings/blob-migration" element={
                    <RoleProtectedRoute allowedRoles={OPERATOR_ROLES}>
                      <BlobMigration />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/settings/project-management/projects" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><Projects /></RoleProtectedRoute>} />
                  <Route path="/settings/projectmanagement/projects" element={<Navigate to="/settings/project-management/projects" replace />} />
                  <Route path="/settings/companies/add" element={<RoleProtectedRoute allowedRoles={OPERATOR_ROLES}><AddEditCompany /></RoleProtectedRoute>} />
                  <Route path="/settings/companies/:id" element={<RoleProtectedRoute allowedRoles={OPERATOR_ROLES}><AddEditCompany /></RoleProtectedRoute>} />
                  {/* Operator-scoped company-employee screens (roster / batch add / welcome emails) were
                      removed: Operator is a system/subscription actor with no company employee-data access
                      (CLAUDE.md Role Model), and the matching APIs now reject it. HR/Admin keep the
                      equivalents at /core-hr/directory, /:company?/users/add-multiple and
                      /core-hr/welcome-emails. */}
                  <Route path="/settings/companies/:companyId/leavesManagement" element={<LegacyLeavesManagementRedirect />} />
                  <Route path="/settings/companies" element={<RoleProtectedRoute allowedRoles={OPERATOR_ROLES}><Companies /></RoleProtectedRoute>} />
                  <Route path="/settings/operator/company-rules" element={
                    <RoleProtectedRoute allowedRoles={OPERATOR_ROLES}>
                      <OperatorCompanyRules />
                    </RoleProtectedRoute>
                  } />
                  {/* Owner decision §14.1c/11 — per-tenant Engagement JOB configuration, moved onto an
                      Operator surface. OPERATOR_ROLES only: the guard mirrors the server, where the write
                      goes to OperatorUpdateCompanyRules (Policy = "Operator") and the picker reads
                      navigation/modules/all?summary=true (Roles = "Operator"). It is CONFIG, not company
                      employee data, so it is inside what the Role Model allows an Operator to touch, and
                      the picker payload is id + name only so it cannot leak employee data as a side
                      effect. The flags themselves stay on CompanyRules and keep failing CLOSED. */}
                  <Route path="/settings/operator/engagement-jobs" element={
                    <RoleProtectedRoute allowedRoles={OPERATOR_ROLES}>
                      <OperatorEngagementJobs />
                    </RoleProtectedRoute>
                  } />
                  {/* Operator leave CONFIGURATION (owner carve-out 2026-07-29): company-level leave types
                      + per-country rules only. Deliberately NOT /leave-policies, which is HR-only and full
                      of employee-scoped surfaces. Guard mirrors navigationModules.ts `ROLES.OPERATOR`. */}
                  <Route path="/settings/operator/leave-configuration" element={
                    <RoleProtectedRoute allowedRoles={OPERATOR_ROLES}>
                      <OperatorLeaveConfiguration />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/settings/operator/subscriptions" element={
                    <RoleProtectedRoute allowedRoles={OPERATOR_ROLES}>
                      <SubscriptionsIndex />
                    </RoleProtectedRoute>
                  } />
                  {/* CO-43. Its own route rather than a fourth tab on the Subscriptions console: an
                      over-cap refund is an SLA-bound queue an operator is sent to directly (from an
                      email, a ticket, a bookmark), and a tab is not linkable that way. Guard mirrors
                      the server's controller-level "Operator" policy on /api/PlatformBilling/*. */}
                  <Route path="/settings/operator/refunds" element={
                    <RoleProtectedRoute allowedRoles={OPERATOR_ROLES}>
                      <RefundQueueIndex />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/settings/operator/platform-terms" element={
                    <RoleProtectedRoute allowedRoles={OPERATOR_ROLES}>
                      <PlatformTermsIndex />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/settings/operator/app-version" element={
                    <RoleProtectedRoute allowedRoles={OPERATOR_ROLES}>
                      <AppVersionIndex />
                    </RoleProtectedRoute>
                  } />
                  {/*
                    🔴 CO-227 — /start-trial IS A REDIRECT, NOT A 404, AND DELIBERATELY SO.
                    Trial is retired and the page is deleted, but the path is baked into emails
                    already sitting in customers' inboxes (SubscriptionTrialExpired,
                    SubscriptionHrUpgradeNudge) and into in-app notification rows that were sent
                    before this release. Those cannot be recalled. A 404 would punish a customer for
                    following a link WE sent them; /settings/billing is where the intent behind that
                    link now leads. Do not remove this until the old mail has aged out.
                  */}
                  <Route path="/start-trial" element={<Navigate to="/settings/billing" replace />} />
                  <Route path="/settings/organizations/add" element={<RoleProtectedRoute allowedRoles={OPERATOR_ROLES}><AddOrganization /></RoleProtectedRoute>} />
                  <Route path="/settings/organizations" element={<RoleProtectedRoute allowedRoles={OPERATOR_ROLES}><Organizations /></RoleProtectedRoute>} />
                  <Route path="/settings/general/attachment-types" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><AttachmentTypes /></RoleProtectedRoute>} />
                  <Route path="/settings/general/attachmentTypes" element={<Navigate to="/settings/general/attachment-types" replace />} />
                  <Route path="/settings/general/date-formats" element={
                    <RoleProtectedRoute allowedRoles={OPERATOR_ROLES}>
                      <DateFormatConfiguration />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/settings/general/dateFormats" element={<Navigate to="/settings/general/date-formats" replace />} />
                  {/* CO-343: OPERATOR_ROLES, not SETTINGS_ROLES. GET /api/Notification/EmailTasks is
                      Policy="Operator" (Program.cs: Operator role only) and the service reads
                      EmailQueuerTasks with NO CompanyId filter — it is deliberately cross-tenant, i.e. a
                      platform surface, which CO-332 made Operator-safe (recipients masked, Subject and
                      ModelJson dropped; ModelJson carried plaintext welcome passwords). With [Hr, Admin]
                      here, Hr reached the screen and got 403 while Operator bounced off the route, so NO
                      role could see it with data. Do NOT "fix" the mismatch by widening the API policy —
                      that would hand every tenant's HR a cross-tenant list. */}
                  <Route path="/settings/system-emails" element={<RoleProtectedRoute allowedRoles={OPERATOR_ROLES}><SystemEmails /></RoleProtectedRoute>} />
                  <Route path="/settings/security-audit" element={<RoleProtectedRoute allowedRoles={SECURITY_AUDIT_ROLES}><SecurityAudit /></RoleProtectedRoute>} />
                  <Route path="/security-audit" element={<Navigate to="/settings/security-audit" replace />} />
                  <Route path="/settings/systemEmails" element={<Navigate to="/settings/system-emails" replace />} />
                  {/* Operator AND a dev build — independent conditions, so both guards, nested.
                      The role check alone let an Operator reach the seed/clear buttons on prod. */}
                  <Route path="/settings/development/seeding" element={<RoleProtectedRoute allowedRoles={OPERATOR_ROLES}><DevToolsRoute><Seeding /></DevToolsRoute></RoleProtectedRoute>} />
                  <Route path="/settings/ai-settings" element={<RoleProtectedRoute allowedRoles={OPERATOR_ROLES}><AISettings /></RoleProtectedRoute>} />
                  {/* 🔴 E12 — Operator-only, on every verb. Guard mirrors the server's
                      Policy = "Operator" on /api/PlatformSettings/*, which PlatformSettingService
                      then re-asserts internally: hiding this route is not what keeps HR out. */}
                  <Route path="/settings/platform-config" element={<RoleProtectedRoute allowedRoles={OPERATOR_ROLES}><PlatformSettings /></RoleProtectedRoute>} />
                  <Route path="/settings/ai-usage" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><AiQuotaOverview /></RoleProtectedRoute>} />
                  <Route path="/settings/notifications" element={<UserNotificationPreferences />} />
                  <Route path="/settings/notification-management" element={
                    <RoleProtectedRoute allowedRoles={SETTINGS_ROLES}>
                      <AdminNotificationManagement />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/settings/module-config" element={<RoleProtectedRoute allowedRoles={OPERATOR_ROLES}><ModuleConfig /></RoleProtectedRoute>} />
                  <Route path="/settings/moduleConfig" element={<Navigate to="/settings/module-config" replace />} />
                  <Route path="/settings/employee-impact/recognition-privacy" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><RecognitionPrivacySettings /></RoleProtectedRoute>} />
                  <Route path="/settings/employee-impact/recognition-values" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><RecognitionValues /></RoleProtectedRoute>} />
                  <Route path="/settings/employeeImpact/privacySettings" element={<Navigate to="/settings/employee-impact/recognition-privacy" replace />} />
                  <Route path="/settings/employeeImpact/recognitionValues" element={<Navigate to="/settings/employee-impact/recognition-values" replace />} />
                  <Route path="/organizations/:id/companies" element={<RoleProtectedRoute allowedRoles={OPERATOR_ROLES}><Companies /></RoleProtectedRoute>} />

                  {/* Tournament Management — canonical kebab-case routes */}
                  <Route path="/tournament-management/tournaments" element={<TournamentList />} />
                  <Route path="/tournament-management/matches" element={<TournamentMatches />} />
                  <Route path="/tournament-management/referee-assignment" element={<RoleProtectedRoute allowedRoles={HR_AND_ADMIN_ROLES}><RefereeAssignmentCenter /></RoleProtectedRoute>} />
                  <Route path="/tournament-management/my-assignments" element={<MyAssignments />} />
                  <Route path="/tournament-management/work-points" element={
                    <RoleProtectedRoute allowedRoles={['Hr', 'Admin']}>
                      <WorkPointSettings />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/tournament-management/referees" element={
                    <RoleProtectedRoute allowedRoles={['Hr', 'Admin']}>
                      <RefereeManagement />
                    </RoleProtectedRoute>
                  } />
                  {/* HR-only: the server gates GetCompensationReport and GeneratePayrollEntries with
                      the HrOnly policy (company-wide salary output + money creation), so an Admin
                      reaching this route would dead-end in a 403. */}
                  <Route path="/tournament-management/compensation" element={
                    <RoleProtectedRoute allowedRoles={HR_ONLY_ROLES}>
                      <Compensation />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/tournament-management/referee-workload" element={
                    <RoleProtectedRoute allowedRoles={['Hr', 'Admin']}>
                      <RefereeWorkload />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/tournament-management/marketplace" element={<MarketplaceHub />} />

                  {/* Back-compat redirects for old camelCase tournament URLs (preserve persisted notifications) */}
                  <Route path="/tournamentManagement/tournaments" element={<Navigate to="/tournament-management/tournaments" replace />} />
                  <Route path="/tournamentManagement/matches" element={<Navigate to="/tournament-management/matches" replace />} />
                  <Route path="/tournamentManagement/referee-assignment" element={<Navigate to="/tournament-management/referee-assignment" replace />} />
                  <Route path="/tournamentManagement/my-assignments" element={<Navigate to="/tournament-management/my-assignments" replace />} />
                  <Route path="/tournamentManagement/work-points" element={<Navigate to="/tournament-management/work-points" replace />} />
                  <Route path="/tournamentManagement/referees" element={<Navigate to="/tournament-management/referees" replace />} />
                  <Route path="/tournamentManagement/compensation" element={<Navigate to="/tournament-management/compensation" replace />} />
                  <Route path="/tournamentManagement/referee-workload" element={<Navigate to="/tournament-management/referee-workload" replace />} />
                  <Route path="/tournamentManagement/marketplace" element={<Navigate to="/tournament-management/marketplace" replace />} />

                  {/* Employee Impact — canonical kebab-case routes */}
                  <Route path="/employee-impact/performance-dashboard" element={<ImpactDashboard />} />
                  <Route path="/employee-impact/engagement-dashboard" element={
                    <RoleProtectedRoute allowedRoles={MANAGER_ROLES}>
                      <InsightsDashboard />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/employee-impact/recognition-and-feedback" element={<Recognition />} />
                  <Route path="/employee-impact/goals" element={<Goals />} />
                  <Route path="/employee-impact/enps" element={<ESPN />} />
                  <Route path="/employee-impact/reflections" element={<Reflections />} />
                  <Route path="/employee-impact/team-hub" element={
                    <RoleProtectedRoute allowedRoles={MANAGER_ROLES}>
                      <TeamHub />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/employee-impact/team-reflections" element={
                    <RoleProtectedRoute allowedRoles={MANAGER_ROLES}>
                      <TeamReflections />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/employee-impact/mission-statements" element={<MissionStatements />} />
                  <Route path="/employee-impact/mentorship" element={<Mentorship />} />
                  {/* Registered BARE until now — no guard at all, while every sibling above has one.
                      Net effect: any authenticated user in a paid tenant with the `impact` module on
                      could open it. Tolerable while the page rendered five invented people; not
                      tolerable now that its picker is the real `PerformanceSnapshot/Roster` and its
                      company tab is real tenant-wide engagement analytics.
                      AI_IMPACT_ROLES is the Performance Snapshot / Core-HR audience: Hr and Admin
                      see the company, Manager and Chief see their own direct reports. Operator is
                      excluded on purpose — this is company employee data (CLAUDE.md Role Model).
                      Defence in depth, not a single gate: `GetRosterAsync` is scoped server-side
                      from claims (Hr/Admin privileged, manager -> direct reports, anyone else `[]`),
                      so the route guard makes the page unreachable and the server scoping contains
                      it even if the guard is ever wrong. The Insights endpoints this page's company
                      tab reads carry only `[Authorize]` server-side, with no role policy — so for
                      that half the route guard is the ONLY gate, which is the other reason it
                      cannot stay bare. */}
                  <Route path="/ai-impact-analysis" element={
                    <RoleProtectedRoute allowedRoles={AI_IMPACT_ROLES}>
                      <AIImpactDashboard />
                    </RoleProtectedRoute>
                  } />

                  {/* Back-compat redirects for renamed Employee Impact routes */}
                  <Route path="/employeeImpact/performanceDashboard" element={<Navigate to="/employee-impact/performance-dashboard" replace />} />
                  <Route path="/employeeImpact/impactDashboard" element={<Navigate to="/employee-impact/performance-dashboard" replace />} />
                  <Route path="/employeeImpact/insights" element={<Navigate to="/employee-impact/engagement-dashboard" replace />} />
                  <Route path="/employeeImpact/recognitionOrFeedback" element={<Navigate to="/employee-impact/recognition-and-feedback" replace />} />
                  <Route path="/employeeImpact/goals" element={<Navigate to="/employee-impact/goals" replace />} />
                  <Route path="/employeeImpact/enps" element={<Navigate to="/employee-impact/enps" replace />} />
                  <Route path="/employeeImpact/reflections" element={<Navigate to="/employee-impact/reflections" replace />} />
                  <Route path="/employeeImpact/teamHub" element={<Navigate to="/employee-impact/team-hub" replace />} />
                  <Route path="/employeeImpact/TeamReflections" element={<Navigate to="/employee-impact/team-reflections" replace />} />
                  <Route path="/employeeImpact/mission-statements" element={<Navigate to="/employee-impact/mission-statements" replace />} />
                  <Route path="/employeeImpact/mentorship" element={<Navigate to="/employee-impact/mentorship" replace />} />
                  <Route path="/ai-impact-dashboard" element={<Navigate to="/ai-impact-analysis" replace />} />

                  {/* Coming Soon - Rich Preview Features */}
                  <Route path="/core-hr/dependents-directory" element={
                    <RoleProtectedRoute allowedRoles={HR_ONLY_ROLES}>
                      <DependentsDirectory />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/core-hr/team-role-management" element={<RoleProtectedRoute allowedRoles={CORE_HR_ROLES}><TeamRoleManagement /></RoleProtectedRoute>} />

                  {/* Coming Soon Routes - Rich Preview Pages */}
                  <Route path="/employee-document-center" element={<EmployeeDocumentCenter />} />
                  {/* Managers hold a real approval stage on internal movements (PendingManagerApproval),
                      and notification type 80 deep-links here — an Hr-only guard bounced the very user the
                      notification was addressed to. The server scopes every read/decision to the caller's
                      own reports, so a manager sees only their team's movements; Hr keeps company scope. */}
                  <Route path="/employment-lifecycle" element={<RoleProtectedRoute allowedRoles={MANAGER_ROLES}><EmploymentLifecycle /></RoleProtectedRoute>} />
                  <Route path="/departments-locations" element={<RoleProtectedRoute allowedRoles={CORE_HR_ROLES}><DepartmentsLocations /></RoleProtectedRoute>} />
                  <Route path="/job-titles-grades" element={<JobTitlesGrades />} />
                  <Route path="/company-files-hr-policies" element={<CompanyFilesPolicies />} />
                  <Route path="/profile-authorization-settings" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><ProfileAuthorization /></RoleProtectedRoute>} />
                  <Route path="/document-template-manager" element={<RoleProtectedRoute allowedRoles={HR_AND_ADMIN_ROLES}><DocumentTemplateManager /></RoleProtectedRoute>} />
                  {/* team-role-management-dashboard redirects to new path */}
                  <Route path="/hr-process-automation" element={<HRProcessAutomation />} />
                  <Route path="/hr-process-automation-center" element={<Navigate to="/hr-process-automation" replace />} />
                  <Route path="/core-hr/reports" element={<RoleProtectedRoute allowedRoles={HR_AND_ADMIN_ROLES}><CoreHRReports /></RoleProtectedRoute>} />
                  <Route path="/attendance-dashboard" element={<AttendanceDashboard />} />
                  <Route path="/duty-roster-management" element={<DutyRosterManagement />} />
                  <Route path="/payroll-run-wizard" element={<PayrollRunWizard />} />
                  <Route path="/payroll-documents-center" element={<PayrollDocuments />} />
                  {/* HR-ONLY (owner decision 2026-09-02): company-wide salary data. Mirrors the server's HrOnly
                      policy on PayrollReportsController and the EOS/Final-Settlement precedent. */}
                  <Route path="/payroll-reports" element={<RoleProtectedRoute allowedRoles={HR_ONLY_ROLES}><PayrollReports /></RoleProtectedRoute>} />
                  {complianceCountry === 'UAE' && (
                    <Route path="/eos-center" element={<EOSCenter />} />
                  )}
                  <Route path="/employee-development-hub" element={<EmployeeDevelopmentHub />} />
                  <Route path="/development-objectives-creator" element={<DevelopmentObjectivesCreator />} />
                  <Route path="/development-objective-detail" element={<DevelopmentObjectiveDetail />} />
                  <Route path="/team-development-dashboard" element={<TeamDevelopmentDashboard />} />
                  <Route path="/manager-development-objectives-creator" element={<Navigate to="/development-objectives-creator" replace />} />
                  <Route path="/development-objective-detail-center" element={<Navigate to="/development-objective-detail" replace />} />
                  <Route path="/manager-team-development-dashboard" element={<Navigate to="/team-development-dashboard" replace />} />
                  <Route path="/check-ins-hub" element={<CheckInsHub />} />
                  {/* CO-436/CO-437 — the snapshot page is no longer an HR/Admin surface. A manager
                      authors it for their own direct reports, and its SUBJECT has to be able to open
                      it: notification type 90 (snapshot submitted) routes the employee through
                      Pending Actions to this page to acknowledge, and an HR_AND_ADMIN_ROLES guard
                      made that a dead end for the very user it was addressed to. The page
                      self-gates — the server returns an EMPTY roster to a non-manager, the view then
                      renders read-only employee mode, and every write re-checks authorization
                      server-side — so the route only has to keep the Operator out (this is company
                      employee data; CLAUDE.md Role Model). EMPLOYEE_SELF_SERVICE_ROLES is exactly
                      that set, same as /recurring-leave above. */}
                  <Route path="/performance-snapshot" element={
                    <RoleProtectedRoute allowedRoles={EMPLOYEE_SELF_SERVICE_ROLES}>
                      <PerformanceSnapshot />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/behavior-and-values" element={<BehaviorValues />} />
                  <Route path="/behavior-values-layer" element={<Navigate to="/behavior-and-values" replace />} />
                  <Route path="/settings/core-hr-settings" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><CoreHRSettings /></RoleProtectedRoute>} />
                  <Route path="/task-board" element={<TaskBoard />} />
                  <Route path="/projects-list" element={<ProjectsList />} />
                  <Route path="/core-hr/bulk-document-upload" element={
                    <RoleProtectedRoute allowedRoles={[...HR_ONLY_ROLES, 'Admin']}>
                      <BulkUploadPage />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/core-hr/assets-management" element={
                    <RoleProtectedRoute allowedRoles={ASSETS_PAGE_ROLES}>
                      <AssetsPage />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/hr-enps-analytics" element={
                    <RoleProtectedRoute allowedRoles={HR_AND_ADMIN_ROLES}>
                      <HRENPSAnalyticsDashboard />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/my-leaves" element={<MyLeaves />} />
                  <Route path="/leave-approvals" element={
                    <RoleProtectedRoute allowedRoles={MANAGER_ROLES}>
                      <LeaveApprovals />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/delegations" element={
                    <RoleProtectedRoute allowedRoles={MANAGER_ROLES}>
                      <DelegationManagement />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/team-calendar" element={
                    <RoleProtectedRoute allowedRoles={MANAGER_ROLES}>
                      <TeamCalendar />
                    </RoleProtectedRoute>
                  } />
                  {/* Re-enabled as the Pending Actions deep-link target for return-to-work items. */}
                  <Route path="/return-to-work" element={
                    <ReturnToWork />
                  } />
                  {/* <Route path="/comp-off" element={
                    <CompOffCredits />
                  } /> */}
                  {/*
                    Short Leave / Leave Encashment are employee SELF-SERVICE pages: every
                    employee can file and cancel their own request, and the approver tabs
                    (Pending Approvals, and Batch Processing on Encashment) are rendered
                    conditionally inside the component. The backing endpoints on
                    LeaveController are plain [Authorize] with the approver/HR checks done in
                    ShortLeaveService / EncashmentService, so the server — not the route — is
                    the security boundary. A MANAGER_ROLES guard here (as on /leave-approvals)
                    would lock out the Clocking employees the feature exists for, so these are
                    intentionally UNGUARDED, matching /my-leaves and /return-to-work.
                  */}
                  <Route path="/short-leave" element={
                    <ShortLeave />
                  } />
                  <Route path="/encashment" element={
                    <Encashment />
                  } />
                  {/* CO-301 — restored. It was parked in `53ea5c8` alongside /return-to-work,
                      /encashment and /short-leave when that batch of leave surfaces was hidden pending a
                      shipping decision; the other three have since been re-enabled one by one. Recurring
                      leave is the last of that batch and is complete on both sides (LeaveController
                      RecurringLeave/{Preview,Submit,MyPatterns,Cancel} + the pattern list/create/cancel
                      view), so it had no reason left to stay hidden — and without it CO-281's type 63/64
                      notifications have no web stimulus at all.
                      CO-365 — it used to be UNGUARDED, on the reasoning that a MANAGER_ROLES gate would
                      lock out the Clocking employees the feature exists for. That is true of
                      MANAGER_ROLES and it was a false dichotomy: "unguarded" also admitted the Operator,
                      who then got the page shell plus a "Failed to load recurring patterns" toast because
                      the endpoint refused a caller with no CompanyId. EMPLOYEE_SELF_SERVICE_ROLES is the
                      set that was actually wanted — everyone except Operator — and it mirrors the
                      Roles list now on LeaveController's RecurringLeave actions. The sidebar entry was
                      already correctly hidden from Operator via the `leaves` module's ROLES.CLOCKING
                      gate; only the typed-URL path was open, which is exactly what this guard closes. */}
                  <Route path="/recurring-leave" element={
                    <RoleProtectedRoute allowedRoles={EMPLOYEE_SELF_SERVICE_ROLES}>
                      <RecurringLeave />
                    </RoleProtectedRoute>
                  } />
                  {/* Self-service surface, deliberately UNGUARDED like /short-leave and /encashment:
                      the RestrictedHoliday pending action is addressed to the employee who must pick
                      their days, so an Hr guard here would bounce every recipient to '/'. HR-only
                      controls (create/open/close list) are gated inside the component and enforced
                      server-side (RestrictedHolidayService's IsInRoleAsync("Hr") checks).
                      The old redirect to /leave-policies was HR-only and made the item un-openable. */}
                  <Route path="/restricted-holidays" element={
                    <RestrictedHolidays />
                  } />
                  <Route path="/statutory-leave-templates" element={
                    <RoleProtectedRoute allowedRoles={HR_ONLY_ROLES}>
                      <StatutoryLeaveTemplates />
                    </RoleProtectedRoute>
                  } />
                  {/* <Route path="/year-end-processing" element={
                    <YearEndProcessing />
                  } /> */}
                  <Route path="/leave-statements" element={
                    <RoleProtectedRoute allowedRoles={HR_ONLY_ROLES}>
                      <LeaveStatements />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/leave-balances" element={
                    <RoleProtectedRoute allowedRoles={HR_ONLY_ROLES}>
                      <LeaveBalances />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/absence-analytics" element={
                    <RoleProtectedRoute allowedRoles={HR_ONLY_ROLES}>
                      <AbsenceAnalytics />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/settings/attendance/leave-policies" element={
                    <Navigate to="/leave-policies" replace />
                  } />
                  <Route path="/leave-policies" element={
                    <RoleProtectedRoute allowedRoles={HR_ONLY_ROLES}>
                      <LeaveSettings />
                    </RoleProtectedRoute>
                  } />

                  {/* Recruitment (UI only — ported from rocketnew) */}
                  <Route path="/recruitment-dashboard" element={
                    <RoleProtectedRoute allowedRoles={[...HR_ONLY_ROLES, 'Admin']}>
                      <RecruitmentDashboard />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/recruitment/job-openings" element={
                    <RoleProtectedRoute allowedRoles={[...HR_ONLY_ROLES, 'Admin']}>
                      <JobOpeningsManagement />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/recruitment/candidates" element={
                    <RoleProtectedRoute allowedRoles={[...HR_ONLY_ROLES, 'Admin']}>
                      <CandidatesManagementCenter />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/recruitment/pipeline" element={
                    <RoleProtectedRoute allowedRoles={[...HR_ONLY_ROLES, 'Admin']}>
                      <HiringPipeline />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/recruitment/interviews" element={
                    <RoleProtectedRoute allowedRoles={[...HR_ONLY_ROLES, 'Admin']}>
                      <InterviewsManagement />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/recruitment/offers" element={
                    <RoleProtectedRoute allowedRoles={[...HR_ONLY_ROLES, 'Admin']}>
                      <OffersManagement />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/settings/recruitment" element={
                    <RoleProtectedRoute allowedRoles={HR_ONLY_ROLES}>
                      <RecruitmentSettings />
                    </RoleProtectedRoute>
                  } />
                  {/* Aliases used by deep-links inside the rocketnew-style pages */}
                  <Route path="/job-openings-management" element={<Navigate to="/recruitment/job-openings" replace />} />
                  <Route path="/candidates-management-center" element={<Navigate to="/recruitment/candidates" replace />} />
                  <Route path="/hiring-pipeline" element={<Navigate to="/recruitment/pipeline" replace />} />
                  <Route path="/interviews-management" element={<Navigate to="/recruitment/interviews" replace />} />
                  <Route path="/offers-management" element={<Navigate to="/recruitment/offers" replace />} />
                  <Route path="/recruitment-settings" element={<Navigate to="/settings/recruitment" replace />} />

                  {/* Module landing redirects.
                      These prefixes appear in `data/lockedRoutes.ts` and in the
                      sidebar's "expand to first child" behavior — but the bare
                      URLs (e.g. /recruitment) never had their own route, so
                      direct navigation rendered a blank page. Redirect each to
                      the canonical first sub-route. Free users still see the
                      LockedShell wrapping the sub-route via MasterLayout. */}
                  <Route path="/recruitment" element={<Navigate to="/recruitment/job-openings" replace />} />
                  <Route path="/payroll" element={<Navigate to="/payroll/dashboard" replace />} />
                  <Route path="/employee-impact" element={<Navigate to="/employee-impact/recognition-and-feedback" replace />} />
                  <Route path="/tournament-management" element={<Navigate to="/tournament-management/tournaments" replace />} />

                  {/* Per-module brochures — Phase 6. Free-tier users hitting a
                      locked module get pointed here from LockedShell + the
                      BrochureOverlay. The router renders the right brochure
                      (Attendance, Leaves, etc.) and falls back to a "coming
                      soon" card for slugs still in flight. */}
                  <Route path="/brochure/:moduleSlug" element={<SubscriptionAudienceRoute><BrochureRouter /></SubscriptionAudienceRoute>} />

                  {/* Public Profile Pages */}
                  <Route path="/profile-settings" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><ProfileSettings /></RoleProtectedRoute>} />
                  <Route path="/profile-configuration-center" element={<ProfileConfigurationCenter />} />
                  <Route path="/profile-configuration-center/:userId" element={<ProfileConfigurationCenter />} />
                  <Route path="/hr-admin-public-profile-settings" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><HRAdminPublicProfileSettings /></RoleProtectedRoute>} />
                  <Route path="/public-profile-display" element={<ErrorBoundary><PublicProfileDisplay /></ErrorBoundary>} />
                </Route>
                </Route>
              </Route>
            </Routes>
            </div>

            {/* AI Assistant - Always available on the side when logged in */}
            {isLoggedIn && <AIAssistant />}

            {/* OTP Toast - Global listener for real-time OTP delivery via SignalR */}
            {isLoggedIn && <OtpToast />}

            {/* Notification Popup - Global listener for real-time notification delivery via SignalR */}
            {isLoggedIn && <NotificationPopup />}

          </I18nextProvider>
    </>
  );
};

const mapStateToProps = (state: any) => {
  return {
    isLoggedIn: state.user.isLoggedIn,
    fullName: state.user.fullName,
    role: state.user.role,
    locale: state.user.locale,
    companyName: state.user.companyName,
    companyLogo: state.user.companyLogo,
    companyLocales: state.user.companyLocales,
    companyPrimaryColor: state.user.companyPrimaryColor,
    currentStatus: state.user.currentStatus,
    usercurrentCustomStatusText: state.user.usercurrentCustomStatusText,
    prevUrl: state.user.prevUrl,
    userId: state.user.userId,
    imageUrl: state.user.imageUrl,
    anyUserWithVariableScheduleRule: state.user.anyUserWithVariableScheduleRule,
    token: state.user.token,
    isManager: state.user.isManager,
    rtl: state.user.rtl,
    billingEnabled: state.user?.company?.billingEnabled,
  };
}

const App = connect(mapStateToProps)(_App);

export default function () {
  return (
    <BrowserRouter>
      <App />
    </BrowserRouter>
  );
}
